import { useCallback, useState } from "react";

type SnoozeValue = number | "forever";
type SnoozeMap = Record<string, SnoozeValue>;

const STORAGE_KEY = "live-banner-snooze";

function getTomorrowMidnight(): number {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function saveToStorage(map: SnoozeMap): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function loadFromStorage(): SnoozeMap {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as SnoozeMap) : {};
  } catch {
    return {};
  }
}

export default function useLiveBannerSnooze() {
  const [snoozeMap, setSnoozeMap] = useState<SnoozeMap>(loadFromStorage());

  const isSnoozed = useCallback(
    (mediaUuid: string): boolean => {
      const now = Date.now();
      const value = snoozeMap[mediaUuid];
      if (value === undefined) return false;

      if (value === "forever") return true;

      if (value > now) return true;

      // Cleanup expired entries on check to avoid localStorage accumulation
      const cleaned = Object.fromEntries(
        Object.entries(snoozeMap).filter(([, v]) => v === "forever" || v > now),
      );

      if (Object.keys(cleaned).length < Object.keys(snoozeMap).length) {
        setSnoozeMap(cleaned);
        saveToStorage(cleaned);
      }

      return false;
    },
    [snoozeMap],
  );

  const setSnoozeEntry = useCallback(
    (mediaUuid: string, value: SnoozeValue): void => {
      setSnoozeMap((prev) => {
        const updated: SnoozeMap = { ...prev, [mediaUuid]: value };
        saveToStorage(updated);
        return updated;
      });
    },
    [],
  );

  const snoozeUntilTomorrow = useCallback(
    (mediaUuid: string): void => {
      setSnoozeEntry(mediaUuid, getTomorrowMidnight());
    },
    [setSnoozeEntry],
  );

  const dismissForever = useCallback(
    (mediaUuid: string): void => {
      setSnoozeEntry(mediaUuid, "forever");
    },
    [setSnoozeEntry],
  );

  return {
    isSnoozed,
    snoozeUntilTomorrow,
    dismissForever,
  };
}

export type { SnoozeMap, SnoozeValue };
